/**
 * The console — not the backend — knows which game iframe this build is paired
 * with: it is baked in at build time as VITE_IFRAME_ORIGIN, per environment.
 *
 * So the preview endpoints hand back the session PIECES (session_token,
 * game_id) and the launch URL is assembled here, in the one layer that owns
 * that knowledge. Shared by the pack-builder preview modal and the Integration
 * page's iframe URLs.
 */

export const MISSING_IFRAME_ORIGIN_MESSAGE =
  'This console build does not know where the game iframe lives: VITE_IFRAME_ORIGIN is not set. ' +
  'Rebuild the console with VITE_IFRAME_ORIGIN pointing at the game for this environment.';

export interface PreviewLaunchPieces {
  session_token: string;
  /** Pack deep link; null/absent launches the lobby. */
  game_id?: number | null;
}

/** Game origin for THIS build, trailing slash trimmed, or null when unset. */
export function iframeOrigin(): string | null {
  const raw: unknown = import.meta.env.VITE_IFRAME_ORIGIN;
  const trimmed = typeof raw === 'string' ? raw.trim() : '';
  if (!trimmed) return null;
  return trimmed.replace(/\/+$/, '');
}

/**
 * Same query shape the partner launch builds server-side
 * (game_platform/v1/sessions_controller#render_launch_response):
 * `<origin>?session_token=…` plus `&game_id=…` for a pack deep link.
 *
 * Throws rather than returning a relative URL: a bare query string is resolved
 * by the browser against the console's own origin, and the preview pane then
 * renders a second copy of the console inside itself.
 */
export function previewLaunchUrl(pieces: PreviewLaunchPieces): string {
  const origin = iframeOrigin();
  if (!origin) throw new Error(MISSING_IFRAME_ORIGIN_MESSAGE);

  const url = `${origin}?session_token=${encodeURIComponent(pieces.session_token)}`;
  return pieces.game_id == null ? url : `${url}&game_id=${encodeURIComponent(String(pieces.game_id))}`;
}
